import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import NavBar from "../../components/NavBar/NavBar";
import { FaDownload, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./Press.css";
import Footer from "../../components/Footer/Footer";
import FloatingWebsiteNavigation from "../../components/FloatingWebsiteNavigation/FloatingWebsiteNavigation";
import { useLanguage } from "../../contexts/LanguageContext";

// Brand assets
import LogoCircular from "../../assets/svgs/logo circular1.png";
import Iwinto from "../../assets/svgs/iwinto.png";
import IwintoWhite from "../../assets/svgs/iwinto white.png";
import LogoCircularBlack from "../../assets/svgs/logo circular black.png";
import LogoCircularWhite from "../../assets/svgs/logo circular white.png";
import LogoTextBlack from "../../assets/svgs/logo text black.png";
import LogoTextWhite from "../../assets/svgs/logo text white.png";

/**
 * Mark the circular logo with a flag so we can style it differently in the lightbox
 */
const ASSETS = [
  { name: "logo circular.png", url: LogoCircular, type: "image", smallInLightbox: true },
  { name: "iwinto.png",         url: Iwinto,       type: "image" },
  { name: "iwinto white.png",   url: IwintoWhite,  type: "image", darkTile: true },
  { name: "logo circular black.png", url: LogoCircularBlack, type: "image", smallInLightbox: true },
  { name: "logo circular white.png", url: LogoCircularWhite, type: "image", darkTile: true, smallInLightbox: true },
  { name: "logo text black.png", url: LogoTextBlack, type: "image" },
  { name: "logo text white.png", url: LogoTextWhite, type: "image", darkTile: true },
];

export default function Press() {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations: t } = useLanguage();
  const [downloadingAll, setDownloadingAll] = useState(false);

  // lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false); // click-to-zoom

  // Get current tab based on pathname
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes('/press/media-kits')) return 'media-kits';
    if (path.includes('/press/inquiries')) return 'inquiries';
    return 'brand-assets';
  };

  const currentTab = getCurrentTab();

  const handleTabClick = (tab) => {
    switch (tab) {
      case 'media-kits':
        navigate('/press/media-kits');
        break;
      case 'inquiries':
        navigate('/press/inquiries');
        break;
      case 'brand-assets':
        navigate('/press');
        break;
      default:
        navigate('/press');
    }
  };

  const brandAssets = useMemo(() => ASSETS, []);

  const downloadOne = useCallback(async (url, filename) => {
    const res = await fetch(url, { cache: "no-cache" });
    if (!res.ok) throw new Error(`Failed to fetch ${filename}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "asset";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  }, []);

  const handleDownloadAll = useCallback(async () => {
    try {
      setDownloadingAll(true);
      for (const item of brandAssets) {
        // eslint-disable-next-line no-await-in-loop
        await downloadOne(item.url, item.name);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 120));
      }
    } catch (e) {
      console.error(e);
      alert("Some files could not be downloaded. Check console for details.");
    } finally {
      setDownloadingAll(false);
    }
  }, [brandAssets, downloadOne]);

  // open/close preview
  const openPreview = useCallback((index) => {
    setCurrentIndex(index);
    setIsZoomed(false);
    setIsOpen(true);
  }, []);
  const closePreview = useCallback(() => {
    setIsOpen(false);
    setIsZoomed(false);
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + brandAssets.length) % brandAssets.length);
    setIsZoomed(false);
  }, [brandAssets.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % brandAssets.length);
    setIsZoomed(false);
  }, [brandAssets.length]);

  // keyboard controls + scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePreview();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden"; // lock scroll
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, closePreview, goPrev, goNext]);

  const active = brandAssets[currentIndex];
  const smallCenter = !!active?.smallInLightbox;

  return (
    <>
      <div className="nav-desktop-only">
        <NavBar />
      </div>
      <div className="press-page">

      <header className="press-hero">
        <div className="press-hero-inner">
          <h1 className="press-title">{t?.press?.title || "Press Center"}</h1>
        </div>
      </header>

      {/* Secondary Navbar / Tabs row */}
      <div className="press-tabs-wrapper">
        <div className="press-tabs">
          <button 
            className={`press-tab ${currentTab === 'media-kits' ? 'active' : ''}`} 
            type="button" 
            aria-label={t?.press?.tabs?.mediaKits || "Media Kits"}
            onClick={() => handleTabClick('media-kits')}
          >
            {(t?.press?.tabs?.mediaKits || "Media Kits").toUpperCase()}
          </button>
          <button
            className={`press-tab ${currentTab === 'brand-assets' ? 'active' : ''}`}
            type="button"
            aria-current={currentTab === 'brand-assets' ? 'page' : undefined}
            aria-label={t?.press?.tabs?.brandAssets || "Brand Assets"}
            onClick={() => handleTabClick('brand-assets')}
          >
            {(t?.press?.tabs?.brandAssets || "Brand Assets").toUpperCase()}
          </button>
          <button 
            className={`press-tab ${currentTab === 'inquiries' ? 'active' : ''}`} 
            type="button" 
            aria-label={t?.press?.tabs?.pressInquiries || "Press Inquiries"}
            onClick={() => handleTabClick('inquiries')}
          >
            {(t?.press?.tabs?.pressInquiries || "Press Inquiries").toUpperCase()}
          </button>
        </div>

        <div className="press-actions">
          <button
            className="download-all-btn"
            type="button"
            onClick={handleDownloadAll}
            disabled={downloadingAll || brandAssets.length === 0}
            aria-label={t?.press?.downloadAll || "Download all brand assets"}
            title={t?.press?.downloadAll || "Download all assets"}
          >
            <FaDownload aria-hidden="true" />
            <span>{downloadingAll ? (t?.press?.downloading || "DOWNLOADING…") : (t?.press?.downloadAll || "DOWNLOAD ALL")}</span>
          </button>
        </div>
      </div>

      {/* Assets grid */}
      <section className="assets-grid" aria-label={t?.press?.assetsGrid || "Brand Assets Grid"}>
        {brandAssets.map((item, i) => (
          <article className="asset-card" key={`${item.name}-${i}`}>
            <div
              className={`asset-thumb ${item.darkTile ? "dark" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => openPreview(i)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPreview(i)}
              aria-label={`${t?.press?.openPreview || "Open preview"}: ${item.name}`}
            >
              <img src={item.url} alt={item.name} draggable={false} />
              <button
                type="button"
                className="asset-download"
                title={t?.press?.download || "Download"}
                aria-label={`${t?.press?.download || "Download"} ${item.name}`}
                onClick={(e) => {
                  e.stopPropagation(); // don't open preview
                  downloadOne(item.url, item.name);
                }}
              >
                <FaDownload />
              </button>
            </div>
            <div className="asset-meta">
              <span className="asset-name">{item.name}</span>
            </div>
          </article>
        ))}
      </section>

      {/* Lightbox */}
      {isOpen && active && (
        <div className="asset-lightbox" onClick={closePreview} role="dialog" aria-modal="true">
          <div
            className="asset-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="lightbox-close" onClick={closePreview} aria-label={t?.press?.close || "Close"} title={t?.press?.close || "Close"}>
              <FaTimes />
            </button>

            <button className="lightbox-nav left" onClick={goPrev} aria-label={t?.press?.previous || "Previous"}>
              <FaChevronLeft />
            </button>
            <button className="lightbox-nav right" onClick={goNext} aria-label={t?.press?.next || "Next"}>
              <FaChevronRight />
            </button>

            {/* Stage: click to zoom; special class for logo circular1 */}
            <div
              className={`lightbox-stage ${active.darkTile ? "dark" : ""} ${isZoomed ? "zoomed" : ""}`}
              onClick={(e) => {
                e.stopPropagation(); // don't close the lightbox when toggling zoom
                setIsZoomed((z) => !z);
              }}
              role="button"
              tabIndex={0}
              aria-label={t?.press?.toggleZoom || "Toggle zoom"}
            >
              <img
                src={active.url}
                alt={active.name}
                className={`lightbox-img ${isZoomed ? "is-zoomed" : ""} ${smallCenter ? "small-center" : ""}`}
                draggable={false}
              />
            </div>

            <div className="lightbox-footer">
              <span className="lightbox-name">{active.name}</span>
              <button
                className="lightbox-download"
                onClick={() => downloadOne(active.url, active.name)}
              >
                <FaDownload />
                <span>{t?.press?.download || "Download"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      </div>
      <FloatingWebsiteNavigation />
    </>
  );
}
